import React, { useContext, useMemo, useRef } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Icon, Layout } from '@oneaudi/unified-web-components';
import type { AudiHeaderDealerProps } from '../../interfaces/header-components.interfaces';
import {
  HeaderStyledDealerDisplay,
  HeaderStyledDealerTextWrapper,
  HeaderStyledDealerLink,
  HeaderStyledDealerName,
  HeaderStyledDealerButton,
  HeaderStyledDealerLabel,
} from './AudiHeaderDealerContextStyles';
import AudiHeaderAdditionalDealerData from './AudiHeaderAdditionalDealerData';
import { truncate } from '../../utils/string-utils';
import { messages } from '../../i18n/messages';
import { DealerContext } from '../../context';
import { getKeyboardFocusableElements, keyTypes, navigate, trapFocus } from '../../utils/a11y';
import useKeyboard from '../../hooks/use-keyboars-events';
import { NAVIGATION_CLASSNAME } from '../../utils/globals';

const AudiHeaderDealerContext: React.FC<AudiHeaderDealerProps> = ({
  isMobileView = false,
  headerNavigationItemsAmount,
  onClickDealerContext,
  showDealerAdditionalData,
  showDealerSearch,
}) => {
  const dealerLabel = useI18n(messages.dealer_label);
  const showDealerInfo = useI18n(messages.dealer_info);
  const hideDealerInfo = useI18n(messages.hide_dealer_info);
  const dealerButtonLabel = showDealerAdditionalData ? hideDealerInfo : showDealerInfo;
  const refDealerData = useRef<HTMLDivElement>(null);
  const arrowIcon = showDealerAdditionalData ? (
    <Icon name="arrow-up" size="s" />
  ) : (
    <Icon name="arrow-down" size="s" />
  );
  const dealerIcon = showDealerAdditionalData ? (
    <Icon name="cancel" size="s" />
  ) : process.env.IS_LEGACY ? (
    <Icon name="my-dealer" size="s" />
  ) : (
    <Icon name="poi-active" size="s" />
  );

  const { dealerData } = useContext(DealerContext);
  const isLegacy = Boolean(process.env.IS_LEGACY);

  const focusedElements = useMemo(() => {
    if (refDealerData.current && showDealerAdditionalData) {
      const focusedElements = getKeyboardFocusableElements(refDealerData.current, true);
      // set initial focus on first element
      requestAnimationFrame(() => {
        setTimeout(() => {
          focusedElements[0].focus();
        }, 200);
      });
      return focusedElements;
    }
    return [];
  }, [refDealerData, showDealerAdditionalData]);

  useKeyboard(
    'keydown',
    (event) => {
      if (refDealerData?.current && focusedElements?.length > 0) {
        trapFocus(focusedElements, event);
        if (event.key === keyTypes.ArrowDown || event.key === keyTypes.ArrowUp) {
          navigate(
            refDealerData.current,
            event.key,
            keyTypes.ArrowDown,
            keyTypes.ArrowUp,
            NAVIGATION_CLASSNAME,
          );
        }
      }
    },
    refDealerData,
  );

  return (
    <>
      <HeaderStyledDealerDisplay
        isMobileView={isMobileView}
        headerNavigationItemsAmount={headerNavigationItemsAmount}
      >
        <HeaderStyledDealerButton
          id="dealerButton"
          isMobileView={isMobileView}
          title={dealerButtonLabel}
          aria-label={dealerButtonLabel}
          type="button"
          onClick={onClickDealerContext}
          data-testid="dealer-data-toggle"
          className="navigation"
        >
          {isLegacy ? (
            <>
              {!isMobileView && (
                <Layout align="flex-start" as="div" margin={[0, '12px', 0, 0]}>
                  {dealerIcon}
                </Layout>
              )}
              <HeaderStyledDealerTextWrapper>
                <HeaderStyledDealerLink>
                  <HeaderStyledDealerLabel isMobileView={isMobileView}>
                    <span>{dealerLabel}</span>
                  </HeaderStyledDealerLabel>
                  <HeaderStyledDealerName isMobileView={isMobileView}>
                    {!isMobileView
                      ? truncate(dealerData?.additionalData?.displayName || dealerData?.name)
                      : dealerData?.additionalData?.displayName ||
                        dealerData?.name ||
                        'Name not found'}
                  </HeaderStyledDealerName>
                </HeaderStyledDealerLink>
              </HeaderStyledDealerTextWrapper>
              {isMobileView && (
                <Layout justify="flex-end" align="flex-end">
                  {arrowIcon}
                </Layout>
              )}
            </>
          ) : (
            <>
              <HeaderStyledDealerTextWrapper>
                <Layout align="flex-start" as="div" margin={[0, '12px', 0, 0]}>
                  {!isMobileView ? dealerIcon : <Icon name="poi-active" size="s" />}
                </Layout>
                <HeaderStyledDealerLink>
                  <HeaderStyledDealerLabel isMobileView={isMobileView}>
                    <span>{dealerLabel}</span>
                  </HeaderStyledDealerLabel>
                  <HeaderStyledDealerName isMobileView={isMobileView}>
                    {!isMobileView
                      ? truncate(dealerData?.additionalData?.displayName || dealerData?.name)
                      : dealerData?.additionalData?.displayName ||
                        dealerData?.name ||
                        'Name not found'}
                  </HeaderStyledDealerName>
                </HeaderStyledDealerLink>
              </HeaderStyledDealerTextWrapper>
              {isMobileView && (
                <Layout justify="flex-end" align="flex-end">
                  {arrowIcon}
                </Layout>
              )}
            </>
          )}
        </HeaderStyledDealerButton>
        <AudiHeaderAdditionalDealerData
          open={showDealerAdditionalData}
          isMobileView={isMobileView}
          refDealerData={refDealerData}
          showDealerSearch={showDealerSearch}
        />
      </HeaderStyledDealerDisplay>
    </>
  );
};

export default AudiHeaderDealerContext;
